import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for scroll-based animations using Intersection Observer API
 * This provides better performance and more accurate detection than scroll position percentages
 */
export const useScrollAnimation = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  const {
    threshold = 0.1, // Trigger when 10% of element is visible
    rootMargin = "0px 0px -10% 0px", // Start animation slightly before element comes into view
    triggerOnce = true, // Only trigger animation once
    delay = 0, // Optional delay before animation starts
  } = options;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              if (triggerOnce) setHasAnimated(true);
            }, delay);
          } else {
            setIsVisible(true);
            if (triggerOnce) setHasAnimated(true);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce, delay]);

  return { ref, isVisible: triggerOnce ? isVisible || hasAnimated : isVisible };
};

/**
 * Hook for section-based navigation that detects which section is currently in view
 * Uses multiple Intersection Observers to track section visibility
 */
export const useSectionNavigation = (sections = []) => {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    if (sections.length === 0) return;

    const observers = sections.map((sectionId) => {
      const element = document.getElementById(sectionId);
      if (!element) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(sectionId);
          }
        },
        {
          // Section is active while it crosses the middle of the viewport.
          // A visible-percentage threshold never fires for sections much taller than the screen.
          threshold: 0,
          rootMargin: "-45% 0px -45% 0px",
        }
      );

      observer.observe(element);
      return observer;
    });

    return () => {
      observers.forEach((observer) => {
        if (observer) observer.disconnect();
      });
    };
  }, [sections]);

  return { activeSection };
};

/**
 * Hook for scroll-based animations that work with percentage-based triggers
 * This is a fallback for components that still need percentage-based detection
 */
export const useScrollPercentage = (thresholds = {}) => {
  const [scrollData, setScrollData] = useState({
    scrollY: 0,
    scrollPercentage: 0,
    isPastThreshold: {},
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercentage = maxHeight > 0 ? scrollY / maxHeight : 0;

      const isPastThreshold = {};
      Object.entries(thresholds).forEach(([key, threshold]) => {
        isPastThreshold[key] = scrollPercentage >= threshold;
      });

      setScrollData({
        scrollY,
        scrollPercentage,
        isPastThreshold,
      });
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledHandleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [thresholds]);

  return scrollData;
};
